import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Sister Properties — curated nearby communities under the same management.
 *
 * Filled for Cambria from the property's sister-community list. Empty slots
 * stay hidden on the live site and show as dashed placeholders in edit mode.
 */

const community1Def: SlotGroupDefinition = {
  label: 'Community 1',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/x_0,y_0,w_1200,h_800,c_crop/q_auto,f_auto,c_lfill,w_576,h_340,g_auto/s3/2/5458/05-310%20w%2045th%20st-7_final.jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Mirabelle Luxury Apartments' },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Mirabelle Luxury Apartments' },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '10901 W 88th St, Overland Park, KS 66214',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.themirabellekc.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community2Def: SlotGroupDefinition = {
  label: 'Community 2',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_576,h_340,g_auto/s3//2/5458/mo_kansascity_46penn_p1098965_01_5_thumbnail.jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '46 Penn Apartment Homes' },
    name: { label: 'Community Name', type: 'text', defaultValue: '46 Penn Apartment Homes' },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '4600 W 46th St, Kansas City, KS 66103',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.46penn.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community3Def: SlotGroupDefinition = {
  label: 'Community 3',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/x_0,y_0,w_3000,h_2000,c_crop/q_auto,f_auto,c_lfill,w_576,h_340,g_auto/s3/2/5458/18-760%20w%2046th%20st-17_final(1).jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'The Dorset' },
    name: { label: 'Community Name', type: 'text', defaultValue: 'The Dorset' },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '760 W 46th St, Kansas City, KS 66103',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.thedorsetapartments.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community4Def: SlotGroupDefinition = {
  label: 'Community 4',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_576,h_340,g_auto/s3//2/5458/mo_kansascity_west39thstreet_p1099358_01_5_thumbnail-2.jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'West 39th Street' },
    name: { label: 'Community Name', type: 'text', defaultValue: 'West 39th Street' },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '3815 State Line Rd, Kansas City, MO 64111',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.west39th.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community5Def: SlotGroupDefinition = {
  label: 'Community 5',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_576,h_340,g_auto/s3//2/5458/mo_kansascity_skyonmain_p1532313_39_5_thumbnail.jpg',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Sky On Main' },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Sky On Main' },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '1300 W 47th St, Kansas City, MO 64112',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.skyonmain.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community6Def: SlotGroupDefinition = {
  label: 'Community 6',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Location', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isFilled(community: Record<string, string | null>): boolean {
  return hasText(community.name) || hasText(community.url) || hasText(community.image);
}

function CommunityCardBody({
  community,
  number,
  isEditing,
  filled,
}: {
  community: Record<string, string | null>;
  number: number;
  isEditing: boolean;
  filled: boolean;
}) {
  const href = (community.url || '').trim();
  const name = (community.name || '').trim();
  const location = (community.location || '').trim();
  const linkLabel = (community.label || 'Visit Community').trim();
  const target = community._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <article
      className={`sister-card${filled ? '' : ' sister-card--empty'}`}
      aria-label={filled ? undefined : `Community ${number} — click to add details`}
    >
      <div className="sister-card-media">
        {hasText(community.image) ? (
          <VibesitesImage
            url={community.image || ''}
            alt={community.alt || name || `Community ${number}`}
            className="sister-card-image"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div className="sister-card-image-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="sister-card-body">
        <h3 className="sister-card-name">
          {name || (isEditing ? `Community ${number}` : '')}
        </h3>
        {location ? (
          <p className="sister-card-location">{location}</p>
        ) : isEditing && !filled ? (
          <p className="sister-card-location sister-card-hint">Add city &amp; state</p>
        ) : null}

        {href ? (
          <a className="sister-card-link" href={href} target={target} rel={rel}>
            {linkLabel}
          </a>
        ) : isEditing ? (
          <span className="sister-card-link sister-card-link--muted">Add website URL</span>
        ) : null}
      </div>
    </article>
  );
}

export function SisterProperties() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);

  const community1 = useSlotGroup('sisterproperties.community1', community1Def);
  const community2 = useSlotGroup('sisterproperties.community2', community2Def);
  const community3 = useSlotGroup('sisterproperties.community3', community3Def);
  const community4 = useSlotGroup('sisterproperties.community4', community4Def);
  const community5 = useSlotGroup('sisterproperties.community5', community5Def);
  const community6 = useSlotGroup('sisterproperties.community6', community6Def);

  return (
    <section ref={sectionRef} className="reveal-fade-up sister-properties">
      <div className="sister-properties-container">
        <div className="sister-properties-grid">
          {(isEditing || isFilled(community1)) && (
            <EditableSlotGroup groupPath="sisterproperties.community1" definition={community1Def}>
              <CommunityCardBody
                community={community1}
                number={1}
                isEditing={isEditing}
                filled={isFilled(community1)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community2)) && (
            <EditableSlotGroup groupPath="sisterproperties.community2" definition={community2Def}>
              <CommunityCardBody
                community={community2}
                number={2}
                isEditing={isEditing}
                filled={isFilled(community2)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community3)) && (
            <EditableSlotGroup groupPath="sisterproperties.community3" definition={community3Def}>
              <CommunityCardBody
                community={community3}
                number={3}
                isEditing={isEditing}
                filled={isFilled(community3)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community4)) && (
            <EditableSlotGroup groupPath="sisterproperties.community4" definition={community4Def}>
              <CommunityCardBody
                community={community4}
                number={4}
                isEditing={isEditing}
                filled={isFilled(community4)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community5)) && (
            <EditableSlotGroup groupPath="sisterproperties.community5" definition={community5Def}>
              <CommunityCardBody
                community={community5}
                number={5}
                isEditing={isEditing}
                filled={isFilled(community5)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community6)) && (
            <EditableSlotGroup groupPath="sisterproperties.community6" definition={community6Def}>
              <CommunityCardBody
                community={community6}
                number={6}
                isEditing={isEditing}
                filled={isFilled(community6)}
              />
            </EditableSlotGroup>
          )}
        </div>

        {isEditing ? (
          <p className="sister-properties-editor-note">
            Click a community card to add its photo, name, location, and website.
            Empty cards stay hidden on the live site.
          </p>
        ) : null}
      </div>

      <style>{`
        .sister-properties {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .sister-properties { padding: 5rem 0 5.5rem; }
        }

        .sister-properties-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sister-properties-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sister-properties-container { padding: 0 2rem; } }

        .sister-properties-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .sister-properties-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .sister-properties-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }

        .sister-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sister-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
        }

        .sister-card--empty {
          border-style: dashed;
          box-shadow: none;
          background: #fafafa;
        }
        .sister-card--empty:hover {
          box-shadow: none;
          border-color: var(--vibe-color-primary);
        }

        .sister-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
        }

        .sister-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .sister-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }

        .sister-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }

        .sister-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }

        .sister-card-location {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        .sister-card-hint {
          font-style: italic;
        }

        .sister-card-link {
          display: inline-flex;
          align-items: center;
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-primary);
          text-decoration: none;
        }
        .sister-card-link:hover {
          text-decoration: underline;
        }
        .sister-card-link--muted {
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-transform: none;
          letter-spacing: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.85rem;
        }

        .sister-properties-editor-note {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin: 1.75rem 0 0;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SisterProperties',
  displayName: 'Sister Properties',
  tokens: {},
  slotGroups: {
    'sisterproperties.community1': community1Def,
    'sisterproperties.community2': community2Def,
    'sisterproperties.community3': community3Def,
    'sisterproperties.community4': community4Def,
    'sisterproperties.community5': community5Def,
    'sisterproperties.community6': community6Def,
  },
};

(SisterProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SisterProperties;
